import React from 'react'
import { motion } from 'framer-motion'
import { DndContext, DragOverlay, MeasuringStrategy } from '@dnd-kit/core'
import { SortableContext } from '@dnd-kit/sortable'
import { TaskEntry } from '../TaskEntry'
import { SectionDropZone } from '../SectionDropZone'
import { ProjectSegment } from '@/src/renderer/features/taskManagement/pages/ProjectPage/components/ProjectSegment'
import { DragOverlayContent } from '@/src/renderer/features/taskManagement/pages/ProjectPage/components/DragOverlayContent'
import type { TaskBoardProps } from './TaskBoard.types'

/**
 * TaskBoard — reusable task board rendering.
 *
 * Renders the hover zone, drag-and-drop context, sortable segments, and drag
 * overlay that ProjectPage and QuickNotesPage share. The host page provides
 * data (tasks, sections, segments) and handlers via props. TaskBoard stays
 * scope-agnostic: it has no knowledge of projects vs quick-notes.
 */
export const TaskBoard: React.FC<TaskBoardProps> = ({
  segments, tasks, projectId, savedName,
  gapAfterSegmentIdx, gapHeight,
  activeDragId, draggedIds, dragCount, isCrossSegmentDrag,
  activeSectionDragId, dragSourceSegIdx, sectionDragWidth, dragRotation, sectionDragYOffset,
  taskListRef, taskListHandleRef, getSegmentHandleRef,
  sensors, customCollisionDetection, allSortableIds, segmentAwareStrategy,
  composedDragStart, composedDragMove, composedDragOver, composedDragEnd, composedDragCancel,
  navigation,
  setHoveredSegmentIndex, setHoveringTasks, activeSegmentIndex, showAddTask,
  creatingSegmentIdx, creatingPosition, creatingAfterTaskId, pendingInlineTarget,
  creationKey, inlineResetKey, skipInlineEntrance, autoSelectId,
  morphData, positionMorphKey, inlineMorphTitle, inlineMorphSelected, inlineMorphKey,
  topCreateRef, bottomCreateRef,
  startCreating,
  handleCreateTask, handleCreateTaskAndContinue,
  handleCreateTaskAfter, handleCreateTaskAfterAndContinue,
  handleCancelCreate, handleMorphComplete, handleInlineMorphComplete,
  autoEditSectionId,
  handleToggleCollapse, handleRenameSection, handleDeleteSection, handleAddSection,
  handleToggleComplete, handleDeleteTask, handleUpdateTask, handleUpdateTaskAndCreateBelow,
  handleDuplicate, handleContextMenuBadgeUpdate,
  optimisticUpdates,
  handleHoverLeave, handleClearSelection,
  sectionsReadonly = false,
  inProjectContext = false,
}) => {
  return (
    <div
      className="project-page__hover-zone"
      onMouseLeave={handleHoverLeave}
      onPointerDown={(e) => {
        const target = e.target as HTMLElement
        // Click on empty space → clear selection.
        // Skip if any dropdown portal is open (Radix or floating-ui based).
        if (document.querySelector('[data-radix-popper-content-wrapper]')) return
        if (document.querySelector('[data-task-dropdown]')) return
        if (
          !target.closest('.task-entry') &&
          !target.closest('.section-header') &&
          !target.closest('.add-task')
        ) {
          handleClearSelection()
        }
      }}
    >
      <div className="project-page__content">
        {/* Segmented task list: sections + task groups with per-segment AddTask */}
        <DndContext
          sensors={sensors}
          collisionDetection={customCollisionDetection}
          onDragStart={composedDragStart}
          onDragMove={composedDragMove}
          onDragOver={composedDragOver}
          onDragEnd={composedDragEnd}
          onDragCancel={composedDragCancel}
          measuring={{ droppable: { strategy: MeasuringStrategy.WhileDragging } }}
          autoScroll={{ layoutShiftCompensation: false }}
        >
          <SortableContext items={allSortableIds} strategy={segmentAwareStrategy}>
            <div
              ref={(el) => {
                (taskListRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
                (navigation.wrapperRef as React.MutableRefObject<HTMLDivElement | null>).current = el
              }}
              className={`project-page__task-list-wrap${activeSectionDragId ? ' project-page__task-list-wrap--section-drag-active' : ''}`}
              tabIndex={-1}
              onKeyDown={navigation.onKeyDown}
              onPointerDownCapture={navigation.onPointerDownCapture}
            >
              {/* 'above' TaskEntry rendered outside segments map so segment key changes
                  (unsectioned segment appearing at index 0) don't break the morph transition. */}
              {((creatingSegmentIdx === 0 && creatingPosition === 'above')
                || morphData?.position === 'above') && (
                <motion.div
                  ref={creatingPosition === 'above' ? topCreateRef : undefined}
                  className="project-page__above-create-entry"
                  initial={false}
                  animate={{ height: 'auto' }}
                  transition={{ height: { duration: 0.25, ease: [0.4, 0, 0.2, 1] } }}
                  style={{ overflow: 'hidden' }}
                >
                  <TaskEntry
                    key={`create-above-${creationKey}`}
                    mode={morphData?.position === 'above' ? 'collapsed' : 'creating'}
                    task={morphData?.position === 'above' ? { id: 'morphing', title: morphData.title, completed: false } : undefined}
                    selected={morphData?.position === 'above' && morphData.selected}
                    onSubmitCreate={creatingPosition === 'above' ? handleCreateTask : undefined}
                    onSubmitCreateAndContinue={creatingPosition === 'above' ? handleCreateTaskAndContinue : undefined}
                    onCancel={creatingPosition === 'above' ? handleCancelCreate : undefined}
                    autoFocus={creatingPosition === 'above'}
                    onMorphComplete={morphData?.position === 'above' ? handleMorphComplete : undefined}
                  />
                </motion.div>
              )}
              {segments.map((segment, i) => (
                <ProjectSegment
                  key={segment.section?.id ?? `unsectioned-${i}`}
                  segment={segment}
                  index={i}
                  isActive={activeSegmentIndex === i}
                  showAddTask={showAddTask}
                  savedName={savedName}
                  totalSegments={segments.length}
                  projectId={projectId}
                  gapAfterSegmentIdx={gapAfterSegmentIdx}
                  gapHeight={gapHeight}
                  activeSectionDragId={activeSectionDragId}
                  dragSourceSegIdx={dragSourceSegIdx}
                  autoEditSectionId={autoEditSectionId}
                  getSectionProps={navigation.getSectionProps}
                  handleToggleCollapse={handleToggleCollapse}
                  handleRenameSection={handleRenameSection}
                  handleDeleteSection={handleDeleteSection}
                  sectionsReadonly={sectionsReadonly}
                  selectSection={navigation.selectSection}
                  selectSectionTasks={navigation.selectSectionTasks}
                  handleSectionNavigateDown={navigation.handleSectionNavigateDown}
                  handleSectionNavigateUp={navigation.handleSectionNavigateUp}
                  creatingSegmentIdx={creatingSegmentIdx}
                  creatingPosition={creatingPosition}
                  morphData={morphData}
                  positionMorphKey={positionMorphKey}
                  inlineMorphTitle={inlineMorphTitle}
                  inlineMorphSelected={inlineMorphSelected}
                  inlineMorphKey={inlineMorphKey}
                  creationKey={creationKey}
                  inlineResetKey={inlineResetKey}
                  skipInlineEntrance={skipInlineEntrance}
                  autoSelectId={autoSelectId}
                  creatingAfterTaskId={creatingAfterTaskId}
                  pendingInlineTarget={pendingInlineTarget}
                  topCreateRef={topCreateRef}
                  bottomCreateRef={bottomCreateRef}
                  startCreating={startCreating}
                  handleAddSection={handleAddSection}
                  handleCreateTask={handleCreateTask}
                  handleCreateTaskAndContinue={handleCreateTaskAndContinue}
                  handleCreateTaskAfter={handleCreateTaskAfter}
                  handleCreateTaskAfterAndContinue={handleCreateTaskAfterAndContinue}
                  handleCancelCreate={handleCancelCreate}
                  handleMorphComplete={handleMorphComplete}
                  handleInlineMorphComplete={handleInlineMorphComplete}
                  handleToggleComplete={handleToggleComplete}
                  handleDeleteTask={handleDeleteTask}
                  handleDuplicate={handleDuplicate}
                  handleContextMenuBadgeUpdate={handleContextMenuBadgeUpdate}
                  handleUpdateTask={handleUpdateTask}
                  handleUpdateTaskAndCreateBelow={handleUpdateTaskAndCreateBelow}
                  navigateEdit={navigation.navigateEdit}
                  getExternalSelection={navigation.getExternalSelection}
                  getSegmentHandleRef={getSegmentHandleRef}
                  taskListHandleRef={taskListHandleRef}
                  draggedIds={draggedIds}
                  activeDragId={activeDragId}
                  dragCount={dragCount}
                  isCrossSegmentDrag={isCrossSegmentDrag}
                  optimisticUpdates={optimisticUpdates}
                  setHoveredSegmentIndex={setHoveredSegmentIndex}
                  setHoveringTasks={setHoveringTasks}
                  inProjectContext={inProjectContext}
                />
              ))}
              {/* Drop zone after the last segment */}
              <SectionDropZone index={segments.length} active={!!activeSectionDragId && segments.length !== dragSourceSegIdx + 1} />
            </div>
          </SortableContext>
          <DragOverlay dropAnimation={null}>
            <DragOverlayContent
              activeSectionDragId={activeSectionDragId}
              activeDragId={activeDragId}
              segments={segments}
              tasks={tasks}
              sectionDragWidth={sectionDragWidth}
              sectionDragOverlayOffset={sectionDragYOffset}
              dragCount={dragCount}
              dragRotation={dragRotation}
            />
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  )
}
